import type { TFunction } from "i18next";

import {
  SupportTicketActivityInitialRequestFlow,
  SupportTicketActivityInitialRequestMigrationFlow,
  SupportTicketActivityInitialRequestOrderFlow,
  SupportTicketActivityInitialRequestOtherFlow,
  SupportTicketActivityInitialRequestSaleFlow,
  SupportTicketActivityInitialRequestVariantFlow,
} from "~/graphql/graphql.gen";
import type { Ticket } from "~/stores/tickets-store";

const ORDER_CANCELLATION = "order/cancellation";
const ORDER_DELAYED = "order/delayed";
const ORDER_DELIVERED_BUT_ITEMS_DAMAGED = "order/delivered_but_items_damaged";
const ORDER_DELIVERED_BUT_ITEMS_MISSING = "order/delivered_but_items_missing";
const ORDER_DELIVERED_BUT_ITEMS_NOT_WORKING_ANYMORE =
  "order/delivered_but_items_not_working_anymore";
const ORDER_DELIVERED_BUT_ITEMS_VOUCHERS_NOT_WORKING =
  "order/delivered_but_items_vouchers_not_working";
const ORDER_DELIVERED_BUT_ITEMS_SIZE_ISSUE =
  "order/delivered_but_items_size_issue";
const ORDER_DELIVERED_BUT_ITEMS_WRONG = "order/delivered_but_items_wrong";
const ORDER_INVOICE_ISSUE = "order/invoice_issue";
const ORDER_OTHER_ISSUE = "order/other_issue";
const ORDER_PAYMENT_METHOD_WRONG = "order/payment_method_wrong";
const ORDER_PICKUP_POINT_ISSUE = "order/pickup_point_issue";
const ORDER_PROMO_CODE_NOT_SET = "order/promo_code_not_set";
const ORDER_REFUND_INCOMPLETE = "order/refund_incomplete";
const ORDER_REFUND_NOT_RECEIVED = "order/refund_not_received";
const ORDER_RETURN_INFO = "order/return_info";
const ORDER_RETURN_INTERNATIONAL = "order/return_international";
const ORDER_RETURN_NOT_AVAILABLE_INFO = "order/return_not_available_info";
const ORDER_RETURN_NOT_ELIGIBLE_INFO = "order/return_not_eligible_info";
const ORDER_RETURNED_TO_SENDER = "order/returned_to_sender";
const ORDER_SHIPPING_ADDRESS_WRONG = "order/shipping_address_wrong";
const ORDER_TRACKING_BLOCKED = "order/tracking_blocked";
const ORDER_TRACKING_DELIVERED_BUT_NOT_RECEIVED =
  "order/tracking_delivered_but_not_received";
const OTHER_ACCOUNT = "other/account";
const OTHER_AFTER_SALE_POLICY = "other/after_sale_policy";
const OTHER_BUG = "other/bug";
const OTHER_CHECKOUT = "other/checkout";
const OTHER_REFERRAL = "other/referral";
const OTHER_REQUEST = "other/request";
const OTHER_SALE = "other/sale";
const SALE_INFO = "sale/info";
const VARIANT_INFO = "variant/info";

export const flowQueryParamMapping = [
  {
    filter: ORDER_CANCELLATION,
    id: SupportTicketActivityInitialRequestFlow.OrderCancelationV1,
  },
  {
    filter: ORDER_DELAYED,
    id: SupportTicketActivityInitialRequestFlow.OrderDelayedV1,
  },
  {
    filter: ORDER_DELIVERED_BUT_ITEMS_DAMAGED,
    id: SupportTicketActivityInitialRequestFlow.OrderDeliveredButItemsDamagedV1,
  },
  {
    filter: ORDER_DELIVERED_BUT_ITEMS_MISSING,
    id: SupportTicketActivityInitialRequestFlow.OrderDeliveredButItemsMissingV1,
  },
  {
    filter: ORDER_DELIVERED_BUT_ITEMS_NOT_WORKING_ANYMORE,
    id: SupportTicketActivityInitialRequestFlow.OrderDeliveredButItemsNotWorkingAnymoreV1,
  },
  {
    filter: ORDER_DELIVERED_BUT_ITEMS_SIZE_ISSUE,
    id: SupportTicketActivityInitialRequestFlow.OrderDeliveredButItemsSizeIssueV1,
  },
  {
    filter: ORDER_DELIVERED_BUT_ITEMS_VOUCHERS_NOT_WORKING,
    id: SupportTicketActivityInitialRequestFlow.OrderDeliveredButItemsVouchersNotWorkingV1,
  },
  {
    filter: ORDER_DELIVERED_BUT_ITEMS_WRONG,
    id: SupportTicketActivityInitialRequestFlow.OrderDeliveredButItemsWrongV1,
  },
  {
    filter: ORDER_INVOICE_ISSUE,
    id: SupportTicketActivityInitialRequestFlow.OrderInvoiceIssueV1,
  },
  {
    filter: ORDER_OTHER_ISSUE,
    id: SupportTicketActivityInitialRequestFlow.OrderOtherIssueV1,
  },
  {
    filter: ORDER_PAYMENT_METHOD_WRONG,
    id: SupportTicketActivityInitialRequestFlow.OrderPaymentMethodWrongV1,
  },
  {
    filter: ORDER_PICKUP_POINT_ISSUE,
    id: SupportTicketActivityInitialRequestFlow.OrderPickupPointIssueV1,
  },
  {
    filter: ORDER_PROMO_CODE_NOT_SET,
    id: SupportTicketActivityInitialRequestFlow.OrderPromoCodeNotSetV1,
  },
  {
    filter: ORDER_REFUND_INCOMPLETE,
    id: SupportTicketActivityInitialRequestFlow.OrderRefundIncompleteV1,
  },
  {
    filter: ORDER_REFUND_NOT_RECEIVED,
    id: SupportTicketActivityInitialRequestFlow.OrderRefundNotReceivedV1,
  },
  {
    filter: ORDER_RETURN_INFO,
    id: SupportTicketActivityInitialRequestFlow.OrderReturnInfoV1,
  },
  {
    filter: ORDER_RETURN_INTERNATIONAL,
    id: SupportTicketActivityInitialRequestFlow.OrderReturnInternationalV1,
  },
  {
    filter: ORDER_RETURN_NOT_AVAILABLE_INFO,
    id: SupportTicketActivityInitialRequestFlow.OrderReturnNotAvailableInfoV1,
  },
  {
    filter: ORDER_RETURN_NOT_ELIGIBLE_INFO,
    id: SupportTicketActivityInitialRequestFlow.OrderReturnNotEligibleInfoV1,
  },
  {
    filter: ORDER_RETURNED_TO_SENDER,
    id: SupportTicketActivityInitialRequestFlow.OrderReturnedToSenderV1,
  },
  {
    filter: ORDER_SHIPPING_ADDRESS_WRONG,
    id: SupportTicketActivityInitialRequestFlow.OrderShippingAddressWrongV1,
  },
  {
    filter: ORDER_TRACKING_BLOCKED,
    id: SupportTicketActivityInitialRequestFlow.OrderTrackingBlockedV1,
  },
  {
    filter: ORDER_TRACKING_DELIVERED_BUT_NOT_RECEIVED,
    id: SupportTicketActivityInitialRequestFlow.OrderTrackingDeliveredButNotReceivedV1,
  },
  {
    filter: OTHER_ACCOUNT,
    id: SupportTicketActivityInitialRequestFlow.OtherAccountV1,
  },
  {
    filter: OTHER_AFTER_SALE_POLICY,
    id: SupportTicketActivityInitialRequestFlow.OtherAfterSalePolicyV1,
  },
  {
    filter: OTHER_BUG,
    id: SupportTicketActivityInitialRequestFlow.OtherBugV1,
  },
  {
    filter: OTHER_CHECKOUT,
    id: SupportTicketActivityInitialRequestFlow.OtherCheckoutV1,
  },
  {
    filter: OTHER_REFERRAL,
    id: SupportTicketActivityInitialRequestFlow.OtherReferralV1,
  },
  {
    filter: OTHER_REQUEST,
    id: SupportTicketActivityInitialRequestFlow.OtherRequestV1,
  },
  {
    filter: OTHER_SALE,
    id: SupportTicketActivityInitialRequestFlow.OtherSaleV1,
  },
  {
    filter: SALE_INFO,
    id: SupportTicketActivityInitialRequestFlow.SaleInfoV1,
  },
  {
    filter: VARIANT_INFO,
    id: SupportTicketActivityInitialRequestFlow.VariantInfoV1,
  },
] as const;

export const flowQueryParamValues = flowQueryParamMapping.map(
  (item) => item.filter,
) as unknown as [
  typeof ORDER_CANCELLATION,
  typeof ORDER_DELAYED,
  typeof ORDER_DELIVERED_BUT_ITEMS_DAMAGED,
  typeof ORDER_DELIVERED_BUT_ITEMS_MISSING,
  typeof ORDER_DELIVERED_BUT_ITEMS_NOT_WORKING_ANYMORE,
  typeof ORDER_DELIVERED_BUT_ITEMS_SIZE_ISSUE,
  typeof ORDER_DELIVERED_BUT_ITEMS_VOUCHERS_NOT_WORKING,
  typeof ORDER_DELIVERED_BUT_ITEMS_WRONG,
  typeof ORDER_INVOICE_ISSUE,
  typeof ORDER_OTHER_ISSUE,
  typeof ORDER_PAYMENT_METHOD_WRONG,
  typeof ORDER_PICKUP_POINT_ISSUE,
  typeof ORDER_PROMO_CODE_NOT_SET,
  typeof ORDER_REFUND_INCOMPLETE,
  typeof ORDER_REFUND_NOT_RECEIVED,
  typeof ORDER_RETURN_INFO,
  typeof ORDER_RETURN_INTERNATIONAL,
  typeof ORDER_RETURN_NOT_AVAILABLE_INFO,
  typeof ORDER_RETURN_NOT_ELIGIBLE_INFO,
  typeof ORDER_RETURNED_TO_SENDER,
  typeof ORDER_SHIPPING_ADDRESS_WRONG,
  typeof ORDER_TRACKING_BLOCKED,
  typeof ORDER_TRACKING_DELIVERED_BUT_NOT_RECEIVED,
  typeof OTHER_ACCOUNT,
  typeof OTHER_AFTER_SALE_POLICY,
  typeof OTHER_BUG,
  typeof OTHER_CHECKOUT,
  typeof OTHER_REFERRAL,
  typeof OTHER_REQUEST,
  typeof OTHER_SALE,
  typeof SALE_INFO,
  typeof VARIANT_INFO,
];

export const getFlows = (t: TFunction) => [
  {
    value: SupportTicketActivityInitialRequestFlow.OrderCancelationV1,
    label: t("flow.order_cancellation"),
  },
  {
    value: SupportTicketActivityInitialRequestFlow.OrderDelayedV1,
    label: t("flow.order_delayed"),
  },
  {
    value:
      SupportTicketActivityInitialRequestFlow.OrderDeliveredButItemsDamagedV1,
    label: t("flow.order_delivered_but_items_damaged"),
  },
  {
    value:
      SupportTicketActivityInitialRequestFlow.OrderDeliveredButItemsMissingV1,
    label: t("flow.order_delivered_but_items_missing"),
  },
  {
    value:
      SupportTicketActivityInitialRequestFlow.OrderDeliveredButItemsNotWorkingAnymoreV1,
    label: t("flow.order_delivered_but_items_not_working_anymore"),
  },
  {
    value:
      SupportTicketActivityInitialRequestFlow.OrderDeliveredButItemsSizeIssueV1,
    label: t("flow.order_delivered_but_items_size_issue"),
  },
  {
    value:
      SupportTicketActivityInitialRequestFlow.OrderDeliveredButItemsVouchersNotWorkingV1,
    label: t("flow.order_delivered_but_items_vouchers_not_working"),
  },
  {
    value:
      SupportTicketActivityInitialRequestFlow.OrderDeliveredButItemsWrongV1,
    label: t("flow.order_delivered_but_items_wrong"),
  },
  {
    value: SupportTicketActivityInitialRequestFlow.OrderInvoiceIssueV1,
    label: t("flow.order_invoice_issue"),
  },
  {
    value: SupportTicketActivityInitialRequestFlow.OrderOtherIssueV1,
    label: t("flow.order_other_issue"),
  },
  {
    value: SupportTicketActivityInitialRequestFlow.OrderPaymentMethodWrongV1,
    label: t("flow.order_payment_method_wrong"),
  },
  {
    value: SupportTicketActivityInitialRequestFlow.OrderPickupPointIssueV1,
    label: t("flow.order_pickup_point_issue"),
  },
  {
    value: SupportTicketActivityInitialRequestFlow.OrderPromoCodeNotSetV1,
    label: t("flow.order_promo_code_not_set"),
  },
  {
    value: SupportTicketActivityInitialRequestFlow.OrderRefundIncompleteV1,
    label: t("flow.order_refund_incomplete"),
  },
  {
    value: SupportTicketActivityInitialRequestFlow.OrderRefundNotReceivedV1,
    label: t("flow.order_refund_not_received"),
  },
  {
    value: SupportTicketActivityInitialRequestFlow.OrderReturnInfoV1,
    label: t("flow.order_return_info"),
  },
  {
    value: SupportTicketActivityInitialRequestFlow.OrderReturnInternationalV1,
    label: t("flow.order_return_international"),
  },
  {
    value:
      SupportTicketActivityInitialRequestFlow.OrderReturnNotAvailableInfoV1,
    label: t("flow.order_return_not_available_info"),
  },
  {
    value: SupportTicketActivityInitialRequestFlow.OrderReturnNotEligibleInfoV1,
    label: t("flow.order_return_not_eligible_info"),
  },
  {
    value: SupportTicketActivityInitialRequestFlow.OrderReturnedToSenderV1,
    label: t("flow.order_returned_to_sender"),
  },
  {
    value: SupportTicketActivityInitialRequestFlow.OrderShippingAddressWrongV1,
    label: t("flow.order_shipping_address_wrong"),
  },
  {
    value: SupportTicketActivityInitialRequestFlow.OrderTrackingBlockedV1,
    label: t("flow.order_tracking_blocked"),
  },
  {
    value:
      SupportTicketActivityInitialRequestFlow.OrderTrackingDeliveredButNotReceivedV1,
    label: t("flow.order_tracking_delivered_but_not_received"),
  },
  {
    value: SupportTicketActivityInitialRequestFlow.OtherAccountV1,
    label: t("flow.other_account"),
  },
  {
    value: SupportTicketActivityInitialRequestFlow.OtherAfterSalePolicyV1,
    label: t("flow.other_after_sale_policy"),
  },
  {
    value: SupportTicketActivityInitialRequestFlow.OtherBugV1,
    label: t("flow.other_bug"),
  },
  {
    value: SupportTicketActivityInitialRequestFlow.OtherCheckoutV1,
    label: t("flow.other_checkout"),
  },
  {
    value: SupportTicketActivityInitialRequestFlow.OtherReferralV1,
    label: t("flow.other_referral"),
  },
  {
    value: SupportTicketActivityInitialRequestFlow.OtherRequestV1,
    label: t("flow.other_request"),
  },
  {
    value: SupportTicketActivityInitialRequestFlow.OtherSaleV1,
    label: t("flow.other_sale"),
  },
  {
    value: SupportTicketActivityInitialRequestFlow.SaleInfoV1,
    label: t("flow.sale_info"),
  },
  {
    value: SupportTicketActivityInitialRequestFlow.VariantInfoV1,
    label: t("flow.variant_info"),
  },
  {
    value: SupportTicketActivityInitialRequestFlow.MigrationV1,
    label: t("flow.migration"),
  },
];

export type Flow = SupportTicketActivityInitialRequestFlow;

export const mapFilterToFlow = (filter: string) => {
  return flowQueryParamMapping.find((item) => item.filter === filter)?.id;
};

export const mapFlowToFilter = (flowId: Flow) => {
  return flowQueryParamMapping.find((item) => item.id === flowId)?.filter;
};

export const getFlow = (initialRequest: Ticket["initialRequest"]) => {
  return initialRequest?.__typename ===
    "SupportTicketActivityInitialRequestOrder"
    ? initialRequest.flow
    : initialRequest?.__typename === "SupportTicketActivityInitialRequestOther"
      ? initialRequest.otherFlow
      : initialRequest?.__typename === "SupportTicketActivityInitialRequestSale"
        ? initialRequest.saleFlow
        : initialRequest?.__typename ===
            "SupportTicketActivityInitialRequestVariant"
          ? initialRequest.variantFlow
          : null;
};

export const mapFlowType = (
  flow:
    | SupportTicketActivityInitialRequestOrderFlow
    | SupportTicketActivityInitialRequestOtherFlow
    | SupportTicketActivityInitialRequestSaleFlow
    | SupportTicketActivityInitialRequestVariantFlow
    | SupportTicketActivityInitialRequestMigrationFlow,
) => {
  switch (flow) {
    case SupportTicketActivityInitialRequestOrderFlow.OrderCancelationV1:
      return SupportTicketActivityInitialRequestFlow.OrderCancelationV1;
    case SupportTicketActivityInitialRequestOrderFlow.OrderDelayedV1:
      return SupportTicketActivityInitialRequestFlow.OrderDelayedV1;
    case SupportTicketActivityInitialRequestOrderFlow.OrderDeliveredButItemsDamagedV1:
      return SupportTicketActivityInitialRequestFlow.OrderDeliveredButItemsDamagedV1;
    case SupportTicketActivityInitialRequestOrderFlow.OrderDeliveredButItemsMissingV1:
      return SupportTicketActivityInitialRequestFlow.OrderDeliveredButItemsMissingV1;
    case SupportTicketActivityInitialRequestOrderFlow.OrderDeliveredButItemsNotWorkingAnymoreV1:
      return SupportTicketActivityInitialRequestFlow.OrderDeliveredButItemsNotWorkingAnymoreV1;
    case SupportTicketActivityInitialRequestOrderFlow.OrderDeliveredButItemsSizeIssueV1:
      return SupportTicketActivityInitialRequestFlow.OrderDeliveredButItemsSizeIssueV1;
    case SupportTicketActivityInitialRequestOrderFlow.OrderDeliveredButItemsVouchersNotWorkingV1:
      return SupportTicketActivityInitialRequestFlow.OrderDeliveredButItemsVouchersNotWorkingV1;
    case SupportTicketActivityInitialRequestOrderFlow.OrderDeliveredButItemsWrongV1:
      return SupportTicketActivityInitialRequestFlow.OrderDeliveredButItemsWrongV1;
    case SupportTicketActivityInitialRequestOrderFlow.OrderInvoiceIssueV1:
      return SupportTicketActivityInitialRequestFlow.OrderInvoiceIssueV1;
    case SupportTicketActivityInitialRequestOrderFlow.OrderOtherIssueV1:
      return SupportTicketActivityInitialRequestFlow.OrderOtherIssueV1;
    case SupportTicketActivityInitialRequestOrderFlow.OrderPaymentMethodWrongV1:
      return SupportTicketActivityInitialRequestFlow.OrderPaymentMethodWrongV1;
    case SupportTicketActivityInitialRequestOrderFlow.OrderPickupPointIssueV1:
      return SupportTicketActivityInitialRequestFlow.OrderPickupPointIssueV1;
    case SupportTicketActivityInitialRequestOrderFlow.OrderPromoCodeNotSetV1:
      return SupportTicketActivityInitialRequestFlow.OrderPromoCodeNotSetV1;
    case SupportTicketActivityInitialRequestOrderFlow.OrderRefundIncompleteV1:
      return SupportTicketActivityInitialRequestFlow.OrderRefundIncompleteV1;
    case SupportTicketActivityInitialRequestOrderFlow.OrderRefundNotReceivedV1:
      return SupportTicketActivityInitialRequestFlow.OrderRefundNotReceivedV1;
    case SupportTicketActivityInitialRequestOrderFlow.OrderReturnInfoV1:
      return SupportTicketActivityInitialRequestFlow.OrderReturnInfoV1;
    case SupportTicketActivityInitialRequestOrderFlow.OrderReturnInternationalV1:
      return SupportTicketActivityInitialRequestFlow.OrderReturnInternationalV1;
    case SupportTicketActivityInitialRequestOrderFlow.OrderReturnNotAvailableInfoV1:
      return SupportTicketActivityInitialRequestFlow.OrderReturnNotAvailableInfoV1;
    case SupportTicketActivityInitialRequestOrderFlow.OrderReturnNotEligibleInfoV1:
      return SupportTicketActivityInitialRequestFlow.OrderReturnNotEligibleInfoV1;
    case SupportTicketActivityInitialRequestOrderFlow.OrderReturnedToSenderV1:
      return SupportTicketActivityInitialRequestFlow.OrderReturnedToSenderV1;
    case SupportTicketActivityInitialRequestOrderFlow.OrderShippingAddressWrongV1:
      return SupportTicketActivityInitialRequestFlow.OrderShippingAddressWrongV1;
    case SupportTicketActivityInitialRequestOrderFlow.OrderTrackingBlockedV1:
      return SupportTicketActivityInitialRequestFlow.OrderTrackingBlockedV1;
    case SupportTicketActivityInitialRequestOrderFlow.OrderTrackingDeliveredButNotReceivedV1:
      return SupportTicketActivityInitialRequestFlow.OrderTrackingDeliveredButNotReceivedV1;
    case SupportTicketActivityInitialRequestOtherFlow.OtherAccountV1:
      return SupportTicketActivityInitialRequestFlow.OtherAccountV1;
    case SupportTicketActivityInitialRequestOtherFlow.OtherAfterSalePolicyV1:
      return SupportTicketActivityInitialRequestFlow.OtherAfterSalePolicyV1;
    case SupportTicketActivityInitialRequestOtherFlow.OtherBugV1:
      return SupportTicketActivityInitialRequestFlow.OtherBugV1;
    case SupportTicketActivityInitialRequestOtherFlow.OtherCheckoutV1:
      return SupportTicketActivityInitialRequestFlow.OtherCheckoutV1;
    case SupportTicketActivityInitialRequestOtherFlow.OtherReferralV1:
      return SupportTicketActivityInitialRequestFlow.OtherReferralV1;
    case SupportTicketActivityInitialRequestOtherFlow.OtherRequestV1:
      return SupportTicketActivityInitialRequestFlow.OtherRequestV1;
    case SupportTicketActivityInitialRequestOtherFlow.OtherSaleV1:
      return SupportTicketActivityInitialRequestFlow.OtherSaleV1;
    case SupportTicketActivityInitialRequestSaleFlow.SaleInfoV1:
      return SupportTicketActivityInitialRequestFlow.SaleInfoV1;
    case SupportTicketActivityInitialRequestVariantFlow.VariantInfoV1:
      return SupportTicketActivityInitialRequestFlow.VariantInfoV1;
    case SupportTicketActivityInitialRequestMigrationFlow.MigrationV1:
      return SupportTicketActivityInitialRequestFlow.MigrationV1;
  }
};
